import isFunction from 'lodash/isFunction';
import startCase from 'lodash/startCase';
import { useCallback, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { v4 } from 'uuid';

import routes from '../../utils/routes';
import useMappingStore from '../hooks/use-mapping-store';
import { DataQueryFormItem } from '../pages/types';
import UiModules from '../ui-modules';
import { fixDates } from '../utils/config-utils';
import { SettingsConfigContext } from './settings-config-context';

export function SettingsContextProviderWrapperRoute() {
  return (
    <SettingsContextProviderWrapper>
      <Outlet />
    </SettingsContextProviderWrapper>
  );
}

export default function SettingsContextProviderWrapper({ children }: { children: JSX.Element }) {
  const navigate = useNavigate();

  const [appConfig, setAppConfig] = useMappingStore(
    'mst.fbd.02.state',
    {
      tabs: [],
      searchConfigs: [],
      previousSearches: [],
      workflows: [],
    },
    fixDates
  );

  const updateAppConfig = useCallback(
    (config: DataQueryFormItem | ((item: DataQueryFormItem) => DataQueryFormItem)) => {
      void setAppConfig((item: DataQueryFormItem) => ({
        ...item,
        ...(isFunction(config) ? config(item) : config),
      }));
    },
    [setAppConfig]
  );

  const updateTabName = useCallback(
    (uuid: string, name: string) => {
      void setAppConfig((u: DataQueryFormItem) => ({
        ...u,
        tabs: u.tabs?.map((tab) => ({ ...tab, name: tab.uuid === uuid ? name : tab.name })),
      }));
    },
    [setAppConfig]
  );

  const getTabById = useCallback(
    (id: string) => {
      return !id ? undefined : appConfig.tabs?.find(({ uuid }) => uuid === id);
    },
    [appConfig]
  );

  const updateTabById = useCallback(
    (uuid: string | undefined, newConfig: DataQueryFormItem | ((data: DataQueryFormItem) => DataQueryFormItem)) => {
      if (!uuid) {
        console.warn('[updateTabById] no UUID is provided!');
        return;
      }

      let seen = false;

      updateAppConfig((u: DataQueryFormItem) => {
        const tabs = u.tabs?.map((tab) => {
          if (tab.uuid !== uuid) {
            return tab;
          }
          seen = true;
          return isFunction(newConfig) ? newConfig(tab) : newConfig;
        });

        return {
          ...u,
          tabs,
        };
      });

      if (!seen) {
        console.warn('[updateTabById] did not find a tab with UUID: ', uuid);
      }
    },
    [updateAppConfig]
  );

  const createTab = useCallback(
    // eslint-disable-next-line @typescript-eslint/require-await
    async (routeType: string, initialConfig?: DataQueryFormItem) => {
      const rawItem = {
        type: routeType.substring(1),
        uuid: v4(),
        name: `New ${startCase(routeType)} Page`,
        ...initialConfig,
      };

      const handler = UiModules[routeType].createItem;
      const newItem = handler(rawItem, { updateAppConfig });
      if (!newItem) {
        return;
      }

      setTimeout(() => {
        void navigate(`${routeType}/${newItem.uuid}`);
      }, 1);
    },
    [updateAppConfig, navigate]
  );

  const closeTab = useCallback(
    (routeToDelete: string) => {
      // expecting e.g. "/workflow/id" while using _shared/routes
      const [, routeType, selectedUuid] = routeToDelete.split('/');

      const handler = UiModules[`/${routeType}`].deleteItem;
      if (handler) {
        return handler(selectedUuid, { updateAppConfig, appConfig }, routeType);
      }

      return false;
    },
    [appConfig, updateAppConfig]
  );

  const closeDataTab = useCallback(
    (dpTableName: string) => {
      const openDataQueryTab = appConfig.tabs?.find((t) => t.config?.dpTableName === dpTableName);
      if (openDataQueryTab?.uuid) {
        return closeTab(`${routes.Data}/${openDataQueryTab?.uuid}`);
      }

      return false;
    },
    [closeTab]
  );

  const value = useMemo(
    () => ({
      appConfig,
      updateAppConfig,
      updateTabName,
      getTabById,
      updateTabById,
      createTab,
      closeTab,
      closeDataTab,
    }),
    [appConfig, updateAppConfig, updateTabName, getTabById, updateTabById, createTab, closeTab, closeDataTab]
  );

  return <SettingsConfigContext.Provider value={value}>{children}</SettingsConfigContext.Provider>;
}
